import React, { useEffect, useId, useRef, useState } from 'react';
import { LuEllipsis } from 'react-icons/lu';
import { cx } from '../../utils/cx.js';
import IconButton from './IconButton';

/**
 * Action menu anchored to its trigger. Items: { id, label, icon?, onClick?, href?, tone?: 'danger',
 * disabled?, description?, divider?, heading? }. Arrow keys move between items, Escape closes.
 * `trigger` is a render prop ({ open, toggle, ref, ...aria }) to replace the default "⋯" button.
 */
// Conditional items leave holes: drop falsy entries and dividers that would open, close or double up.
const tidy = (items) => items.filter(Boolean).filter((item, index, list) => {
  if (!item.divider) return true;
  const previous = list[index - 1];
  return index > 0 && index < list.length - 1 && !previous?.divider;
});

const DropdownMenu = ({ items: rawItems = [], label = 'Plus d\'actions', align = 'right', width = 'w-64', trigger = null, className = '' }) => {
  const items = tidy(rawItems);
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const menuId = useId();

  const close = (restoreFocus = false) => {
    setOpen(false);
    if (restoreFocus) triggerRef.current?.focus();
  };

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    const t = setTimeout(() => rootRef.current?.querySelector('[role="menuitem"]:not([aria-disabled="true"])')?.focus(), 0);
    return () => {
      document.removeEventListener('mousedown', onDown);
      clearTimeout(t);
    };
  }, [open]);

  const onMenuKeyDown = (e) => {
    const nodes = [...rootRef.current.querySelectorAll('[role="menuitem"]:not([aria-disabled="true"])')];
    const index = nodes.indexOf(document.activeElement);
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      close(true);
    } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const next = e.key === 'ArrowDown' ? (index + 1) % nodes.length : (index - 1 + nodes.length) % nodes.length;
      nodes[next]?.focus();
    } else if (e.key === 'Tab') {
      setOpen(false);
    }
  };

  const toggle = () => setOpen((o) => !o);
  const ariaProps = { 'aria-haspopup': 'menu', 'aria-expanded': open, 'aria-controls': open ? menuId : undefined };

  return (
    <div ref={rootRef} className={cx('relative inline-flex', className)}>
      {trigger
        ? trigger({ open, toggle, ref: triggerRef, ...ariaProps })
        : <IconButton ref={triggerRef} icon={LuEllipsis} label={label} variant="outline" size="md" onClick={toggle} {...ariaProps} />}

      {open && (
        <div
          id={menuId}
          role="menu"
          aria-label={label}
          onKeyDown={onMenuKeyDown}
          className={cx(
            'absolute top-full z-40 mt-1.5 rounded-xl border border-gray-200 bg-white p-1.5 text-left shadow-xl dark:border-gray-800 dark:bg-night-750',
            align === 'right' ? 'right-0' : 'left-0',
            width,
          )}
        >
          {items.map((item, index) => {
            if (item.divider) return <div key={`divider-${index}`} className="my-1.5 border-t border-gray-100 dark:border-gray-800" role="separator" />;
            if (item.heading) {
              return <div key={`heading-${index}`} className="px-2.5 pb-1 pt-1.5 text-[10px] font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500">{item.heading}</div>;
            }
            if (item.note) {
              return <p key={`note-${index}`} className="px-2.5 py-2 text-xs italic text-gray-500 dark:text-gray-400">{item.note}</p>;
            }
            const Icon = item.icon;
            const danger = item.tone === 'danger';
            const Tag = item.href ? 'a' : 'button';
            return (
              <Tag
                key={item.id || index}
                role="menuitem"
                tabIndex={-1}
                type={item.href ? undefined : 'button'}
                href={item.href}
                target={item.href && item.external ? '_blank' : undefined}
                rel={item.href && item.external ? 'noopener noreferrer' : undefined}
                aria-disabled={item.disabled || undefined}
                onClick={(e) => {
                  if (item.disabled) {
                    e.preventDefault();
                    return;
                  }
                  close();
                  item.onClick?.(e);
                }}
                className={cx(
                  'flex w-full items-start gap-2.5 rounded-lg px-2.5 py-2 text-left text-sm outline-none transition-colors',
                  item.disabled
                    ? 'cursor-not-allowed opacity-50'
                    : danger
                      ? 'cursor-pointer text-red-600 hover:bg-red-50 focus:bg-red-50 dark:text-red-400 dark:hover:bg-red-500/10 dark:focus:bg-red-500/10'
                      : 'cursor-pointer text-gray-700 hover:bg-gray-100 focus:bg-gray-100 dark:text-gray-200 dark:hover:bg-white/5 dark:focus:bg-white/5',
                )}
              >
                {Icon && <Icon className={cx('mt-0.5 h-4 w-4 flex-shrink-0', danger ? '' : 'text-gray-400 dark:text-gray-500')} aria-hidden />}
                <span className="min-w-0 flex-1">
                  <span className="block font-medium">{item.label}</span>
                  {item.description && <span className="mt-0.5 block text-xs text-gray-500 dark:text-gray-400">{item.description}</span>}
                </span>
              </Tag>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default DropdownMenu;
